import React, { useState, useEffect, useRef, useCallback, createContext, useContext } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate, useLocation, useNavigate, Outlet } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { companyService } from './services/companyService';
import { getDefaultDashboard } from './utils/roleAccess';
import useDebugMode from './hooks/useDebugMode';
import { supabase } from './lib/supabase';

import CompanyRegistration from './pages/company-registration';
import AdminDashboard from './pages/admin-dashboard';
import Login from './pages/login';
import DocumentManagement from './pages/document-management';
import SiteDashboard from './pages/site-dashboard';
import SiteSetupWizard from './pages/site-setup-wizard';
import ComplianceReport from './pages/compliance-report';
import IncidentReporting from './pages/incident-reporting';
import DailySiteChecklist from './pages/daily-site-checklist';
import AuditsInspections from './pages/audits-inspections';
import DrawingsPlans from './pages/drawings-plans';
import SitesList from './pages/sites-list';
import SiteDetailDashboard from './pages/site-detail-dashboard';
import SiteSettings from './pages/site-settings';
import DocumentDetail from './pages/document-detail';
import ChecklistManagement from './pages/checklist-management';
import NotificationsCenter from './pages/notifications-center';

import WorkersList from './pages/workers-list';
import WorkerDetail from './pages/worker-detail';
import SetupFlowCompanyDetails from './pages/setup-flow-company-details';
import SetupFlowPlanConfirmation from './pages/setup-flow-plan-confirmation';
import SetupFlowCreateSite from './pages/setup-flow-create-site';
import SetupFlowChecklistConfirmation from './pages/setup-flow-checklist-confirmation';
import SetupFlowInviteTeam from './pages/setup-flow-invite-team';
import ProfileSettings from './pages/profile-settings';
import CompanySettings from './pages/company-settings';
import HelpSupport from './pages/help';
import WorkerHome from './pages/worker-home';
import WorkerWelcomeLanding from './pages/worker-welcome-landing';
import AdminWorkerAssignment from './pages/admin-worker-assignment';
import CameraFirstPhotoUpload from './pages/camera-first-photo-upload';
import EnhancedAdminDashboard from './pages/enhanced-admin-dashboard';
import SiteManagerDashboard from './pages/site-manager-dashboard';
import ReportBuilder from './pages/report-builder';
import TaskPlanner from './pages/task-planner';
import TaskCompletionWorkflow from './pages/task-completion-workflow';
import TaskAnalyticsDashboard from './pages/task-analytics-dashboard';
import PhotoUploadPrompt from './pages/photo-upload-prompt';
import SitePhotoGallery from './pages/site-photo-gallery';
import SnagsList from './pages/snags-list-creation';
import SnagDetailView from './pages/snag-detail-view';
import ClientDashboard from './pages/client-dashboard';
import DrawingViewer from './pages/drawing-viewer';
import DrawingDetail from './pages/drawing-detail';
import DrawingsGallery from './pages/drawings-gallery';
import BudgetReport from './pages/budget-report';
import InviteUsers from './pages/invite-users';
import QATestingDashboard from './pages/qa-testing-dashboard';
import SubscriptionManagement from './pages/subscription-management';
import InvoiceHistory from './pages/invoice-history';
import PaywallGuard from './components/PaywallGuard';
import CsvImportCenter from './pages/csv-import-center';
import LandingPage from './pages/landing';
import EmailVerificationPending from './pages/email-verification-pending';
import ResetPassword from './pages/reset-password';
import AuthCallback from './pages/auth-callback';
import EmailVerified from './pages/email-verified';
import DemoLogin from './pages/demo-login/index';
import RamsManagement from './pages/rams-management';
import RamsWorkerView from './pages/rams-worker-view';
import SiteInductionsManagement from './pages/site-inductions-management';
import SiteInductionsWorkerView from './pages/site-inductions-worker-view';
import ToolboxTalksManagement from './pages/toolbox-talks-management';
import ToolboxTalksWorkerView from './pages/toolbox-talks-worker-view';
import SiteDiaryManagement from './pages/site-diary-management';
import WorkerCertificationsManagement from './pages/worker-certifications';
import ComplianceDashboard from './pages/compliance-dashboard';
import NearMissesPage from './pages/near-misses';
import TermsAndConditions from './pages/terms-and-conditions';
import PrivacyPolicy from './pages/privacy-policy';
import DataProcessingAgreement from './pages/data-processing';
import TermsOfService from './pages/terms-of-service';
import CookiePolicy from './pages/cookie-policy';
import DailySiteReport from './pages/daily-site-report';
import AdminActivityLog from './pages/admin-activity-log';
import WorkerSchedule from './pages/worker-schedule';
import AcceptInvite from './pages/accept-invite';
import OpsOverview from './pages/ops';
import OpsCompanyDetail from './pages/ops/CompanyDetail';
import InviteSetup from './pages/invite-setup';
import PWASplashScreen from './components/PWASplashScreen';
import CheckoutSuccess from './pages/checkout-success';

// ── AUTH DECISION CONTEXT ──────────────────────────────────────────────────
// Single resolved auth state shared across all route guards.
export const AuthDecisionContext = createContext(null);
export const useAuthDecision = () => useContext(AuthDecisionContext);

// ── DEBUG OVERLAY ──────────────────────────────────────────────────────────
const AuthDebugOverlay = ({ label, data }) => {
  const debugMode = useDebugMode();
  const [visible, setVisible] = useState(true);

  if (!debugMode) return null;

  if (!visible) return (
    <button
      onClick={() => setVisible(true)}
      style={{ position: 'fixed', bottom: 8, right: 8, zIndex: 99999, background: '#1e3a5f', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer' }}
    >🔍 Debug</button>
  );
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 99999,
      background: '#0f172a', color: '#e2e8f0', padding: '10px 14px',
      fontFamily: 'monospace', fontSize: '11px', borderBottom: '2px solid #3b82f6',
      maxHeight: '40vh', overflowY: 'auto',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <strong style={{ color: '#60a5fa' }}>🔍 AUTH DEBUG — {label}</strong>
        <button onClick={() => setVisible(false)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 14 }}>✕</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '3px 16px' }}>
        {Object.entries(data)?.map(([k, v]) => (
          <span key={k}>
            <span style={{ color: '#94a3b8' }}>{k}:</span>{' '}
            <code style={{ color: v === null || v === false || v === 'NOT FOUND' || v === 'BLOCKED' ? '#f87171' : v === true || v === 'FOUND' || v === 'ALLOWED' || v === 'MOUNTED' ? '#4ade80' : '#fbbf24' }}>
              {v === null ? 'null' : v === undefined ? 'undefined' : String(v)}
            </code>
          </span>
        ))}
      </div>
    </div>
  );
};

// ── DEBUG PAGE MOUNT TRACKER ───────────────────────────────────────────────
const DebugPageMount = ({ name, children }) => {
  const debugMode = useDebugMode();
  const [mounted, setMounted] = useState(false);
  const location = useLocation();
  useEffect(() => {
    setMounted(true);
    console.log(`[DebugPageMount] ${name} MOUNTED at ${location?.pathname}`);
  }, []);

  if (!debugMode) return children;

  return (
    <>
      <AuthDebugOverlay
        label={`PAGE MOUNT — ${name}`}
        data={{
          guard: 'DebugPageMount',
          pageName: name,
          pageMounted: mounted ? 'MOUNTED' : 'NOT YET',
          pathname: location?.pathname,
        }}
      />
      {children}
    </>
  );
};

// ── SINGLE CENTRAL AUTH ROUTER ─────────────────────────────────────────────
// Wraps the ENTIRE route tree as a stable parent — never mounted inside a
// <Route> element, so it is NEVER remounted on navigation. This is the
// critical fix: previously AuthRouter was the element of <Route path="/*">,
// which caused React Router to remount it on every navigation, resetting
// resolvedRef and subscriptionActive to false and triggering a redirect
// window that sent onboarded users back to /setup-flow-plan-confirmation.
//
// Module-level cache: persists across any accidental remounts.
// Key: userId. Value: { hasCompany, subscriptionActive }.
// Once subscriptionActive=true is written for a user, it is NEVER downgraded
// within the same browser session.
const _resolvedCache = {};

// ── Subscription state classification ─────────────────────────────────────
// READ_ONLY_STATUSES: company kept in the app with a soft-lock banner and
// (in PR 2) gated create surfaces. paused covers the no-card-trial path
// from B9UB0M58 — Stripe already accepts it on this column per
// 20260328120000_fix_subscription_status_constraint.sql.
// ACTIVE_STATUSES: the union with the truly-active states. Subscription is
// considered "active enough to render the app" if it's in this set.
// Anything outside both sets (inactive, NULL, incomplete, incomplete_expired)
// routes through PaywallGuard to the setup flow / billing recovery.
const READ_ONLY_STATUSES = ['past_due', 'unpaid', 'canceled', 'paused'];
const ACTIVE_STATUSES = ['active', 'trialing', ...READ_ONLY_STATUSES];

const AuthRouter = ({ children }) => {
  const { user, userProfile, loading, roleLoading, isDemoAdmin } = useAuth();
  const location = useLocation();

  const [resolved, setResolved] = useState(false);
  const [hasCompany, setHasCompany] = useState(false);
  const [subscriptionActive, setSubscriptionActive] = useState(false);
  const [subscriptionReadOnly, setSubscriptionReadOnly] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [timedOut, setTimedOut] = useState(false);
  const resolvedRef = useRef(false);

  // Reset resolution when the user changes (login/logout).
  // Without this, AuthRouter resolves once for a logged-out user (hasCompany=false,
  // subscriptionActive=false) and never re-resolves when the user signs in.
  const prevUserIdRef = useRef(user?.id ?? null);
  useEffect(() => {
    const currentUserId = user?.id ?? null;
    if (currentUserId !== prevUserIdRef.current) {
      prevUserIdRef.current = currentUserId;
      if (resolvedRef.current) {
        console.log('[AuthRouter] User changed — resetting resolution', { from: prevUserIdRef.current, to: currentUserId });
        resolvedRef.current = false;
        setResolved(false);
        setHasCompany(false);
        setSubscriptionActive(false);
        setSubscriptionReadOnly(false);
        setSubscriptionStatus(null);
        setTimedOut(false);
      }
    }
  }, [user?.id]);

  // Hard 8s timeout — app can never hang indefinitely
  useEffect(() => {
    if (resolved) return; // already resolved, no timeout needed
    const t = setTimeout(() => {
      if (!resolvedRef?.current) {
        console.warn('[AuthRouter] Hard timeout after 8s — forcing resolution');
        setTimedOut(true);
        setResolved(true);
        resolvedRef.current = true;
      }
    }, 8000);
    return () => clearTimeout(t);
  }, [resolved]);

  // Re-fetches the user's company row and updates the cached AuthDecision.
  //
  // Used by:
  //   - The first-resolution effect below (initial login / page load).
  //   - The visibility-change listener below (covers "user paid in another
  //     tab and came back" + analogous downgrade scenarios — see card
  //     mGV9jFtd).
  //
  // Semantics on success vs. error:
  //   - Successful fetch (real company row OR genuine "no company found")
  //     trusts the new values, allowing downgrades from true → false. This
  //     is the change vs. the prior unconditional no-downgrade rule. Without
  //     it, a Stripe webhook flipping the user to canceled / past_due wasn't
  //     reflected until logout.
  //   - Transient error (timeout, RPC failure, network error) preserves the
  //     prior cached values to avoid bouncing a paying user out on a hiccup.
  //     This is the same defence-in-depth that the original `||` no-downgrade
  //     rule was reaching for; we just no longer collapse all not-yet-active
  //     fetches into "preserve" — only the actually-failing ones.
  //
  // Does NOT touch resolvedRef / setResolved — those are first-resolution
  // semantics that the caller is responsible for. Refresh-on-visibility must
  // not flip `resolved` back to false (that would cause a UI flicker through
  // the AuthRouter loading spinner mid-session).
  const refreshAuthDecision = useCallback(async () => {
    if (!user) return;
    if (!userProfile) return;
    const userId = user?.id;
    const role = userProfile?.role;

    if (role !== 'admin' || isDemoAdmin) {
      // Non-admin roles or demo admin — no billing state to fetch.
      // Keep the cache + state in sync (idempotent on repeat calls).
      setHasCompany(true);
      setSubscriptionActive(true);
      _resolvedCache[userId] = { hasCompany: true, subscriptionActive: true };
      return;
    }

    try {
      // Promise.race with a rejecting timeout (instead of resolving { data:
      // null }) so a slow company fetch is treated as a transient error in
      // the catch block below — preserving prior cached values rather than
      // false-downgrading the user.
      const result = await Promise.race([
        companyService?.getUserCompany(),
        new Promise((_, reject) => setTimeout(() => reject(new Error('AuthRouter company fetch timed out')), 5000)),
      ]);

      // companyService returns `{ data: null, error: { message: 'No company found…' } }`
      // when the user genuinely has no company yet (newly registered admin
      // pre-setup-flow). Treat that as a successful fetch with no company —
      // newHasCompany resolves to false and the user routes to setup. Other
      // error shapes (RPC failure across all fallbacks, etc.) are transient
      // and should preserve the prior cached values, so re-throw.
      if (result?.error && !result?.data) {
        const msg = result?.error?.message || '';
        if (!/no company found/i.test(msg)) {
          throw new Error(`Company fetch failed: ${msg}`);
        }
      }

      const company = result?.data;
      const newHasCompany = !!(company?.id);
      const subStatus = company?.subscription_status;
      // subscriptionActive is derived strictly from billing state.
      // setup_complete/onboarding_finished are NOT included — they describe
      // setup progression, not payment. Including them was a bypass vector.
      // Read-only states (past_due, unpaid, canceled, paused) keep the app
      // rendered so the user can see their data; writes are gated via
      // subscriptionReadOnly. See READ_ONLY_STATUSES / ACTIVE_STATUSES above.
      const newSubscriptionActive = ACTIVE_STATUSES.includes(subStatus);
      const newSubscriptionReadOnly = READ_ONLY_STATUSES.includes(subStatus);
      // neverPaid: user has never completed a Stripe checkout.
      // Used to route inactive users to setup (never paid) vs billing recovery
      // (previously paid). Canceled subscriptions null out stripe_subscription_id
      // on cancellation (see stripe-webhook customer.subscription.deleted), so
      // the explicit subStatus check guards against false-classifying them.
      const newNeverPaid = !company?.stripe_subscription_id && subStatus !== 'canceled';

      // Write fresh values straight through — no `||` no-downgrade merge.
      // Successful fetches are authoritative; downgrades are intentional.
      _resolvedCache[userId] = {
        hasCompany: newHasCompany,
        subscriptionActive: newSubscriptionActive,
        subscriptionReadOnly: newSubscriptionReadOnly,
        subscriptionStatus: subStatus ?? null,
        neverPaid: newNeverPaid,
      };

      setHasCompany(newHasCompany);
      setSubscriptionActive(newSubscriptionActive);
      setSubscriptionReadOnly(newSubscriptionReadOnly);
      setSubscriptionStatus(subStatus ?? null);
      console.log('[AuthRouter] admin company lookup:', company?.id || 'NOT FOUND', '| subscription_status=', subStatus, '| subscriptionActive=', newSubscriptionActive, '| subscriptionReadOnly=', newSubscriptionReadOnly, '| neverPaid=', newNeverPaid);
    } catch (err) {
      console.error('[AuthRouter] company lookup error:', err);
      // Transient error — preserve prior cached values rather than
      // false-downgrading a paying user on a network hiccup or timeout.
      const prevCached = _resolvedCache?.[userId];
      if (prevCached) {
        setHasCompany(prevCached?.hasCompany);
        setSubscriptionActive(prevCached?.subscriptionActive);
        setSubscriptionReadOnly(prevCached?.subscriptionReadOnly ?? false);
        setSubscriptionStatus(prevCached?.subscriptionStatus ?? null);
      } else {
        setHasCompany(false);
        setSubscriptionActive(false);
        setSubscriptionReadOnly(false);
        setSubscriptionStatus(null);
      }
    }
  }, [user, userProfile, isDemoAdmin]);

  useEffect(() => {
    // Already resolved — never re-run
    if (resolvedRef?.current) return;

    // Still waiting for auth/profile to load
    if (loading || roleLoading) return;

    // Auth loaded but no user — resolve immediately
    if (!user) {
      resolvedRef.current = true;
      setResolved(true);
      return;
    }

    // User present but profile not yet loaded — wait (timeout will unblock)
    if (!userProfile) return;

    const userId = user?.id;

    // Check module-level cache first — if we already resolved
    // subscriptionActive=true for this user in this browser session, reuse
    // it immediately without a DB fetch. Visibility-driven refreshes update
    // this cache in-place, so a hit here is the most-recent known value.
    if (_resolvedCache?.[userId]) {
      const cached = _resolvedCache?.[userId];
      console.log('[AuthRouter] Using cached resolution for user', userId, '| subscriptionActive=', cached?.subscriptionActive);
      setHasCompany(cached?.hasCompany);
      setSubscriptionActive(cached?.subscriptionActive);
      setSubscriptionReadOnly(cached?.subscriptionReadOnly ?? false);
      setSubscriptionStatus(cached?.subscriptionStatus ?? null);
      resolvedRef.current = true;
      setResolved(true);
      return;
    }

    (async () => {
      await refreshAuthDecision();
      resolvedRef.current = true;
      setResolved(true);
    })();
  }, [loading, roleLoading, user, userProfile, isDemoAdmin, refreshAuthDecision]);

  // Refresh subscription state when the user returns to the tab. Catches the
  // realistic "paid in another tab" / "subscription cancelled while I was
  // away" scenarios without needing a logout. Throttled to 30s so rapid tab
  // cycling doesn't produce a refresh storm. Per card mGV9jFtd.
  useEffect(() => {
    if (!user) return;
    let lastRefresh = 0;
    const THROTTLE_MS = 30_000;

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastRefresh < THROTTLE_MS) return;
      lastRefresh = now;
      refreshAuthDecision();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user, refreshAuthDecision]);

  // Compute the canonical destination once resolved
  const getDestination = () => {
    if (!user) return '/login';
    if (!userProfile) return '/login';

    const role = userProfile?.role;

    if (role === 'admin') {
      if (isDemoAdmin) return '/admin-dashboard';
      if (!hasCompany) return '/setup-flow-plan-confirmation';
      if (subscriptionActive) return '/admin-dashboard';
      // Inactive admin: never-paid → resume setup, previously-paid → billing recovery
      const cached = _resolvedCache?.[user?.id];
      return cached?.neverPaid ? '/setup-flow-plan-confirmation' : '/subscription-management';
    }
    if (role === 'site_manager') return '/site-manager-dashboard';
    if (role === 'worker') return '/worker-today';
    if (role === 'client') return '/client-dashboard';
    return getDefaultDashboard(role);
  };

  const destination = resolved ? getDestination() : null;

  const debugData = {
    pathname: location?.pathname,
    guard: 'AuthRouter',
    resolved,
    timedOut,
    authUser: user ? 'FOUND' : 'NOT FOUND',
    role: userProfile?.role || 'NOT FOUND',
    hasCompany: hasCompany ? 'FOUND' : 'NOT FOUND',
    loading,
    roleLoading,
    destination: destination || 'PENDING',
  };

  if (!resolved) {
    return (
      <div className="flex items-center justify-center h-screen">
        <AuthDebugOverlay label="AuthRouter — RESOLVING" data={debugData} />
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    );
  }

  const contextValue = {
    resolved,
    user,
    userProfile,
    hasCompany,
    subscriptionActive,
    subscriptionReadOnly,
    subscriptionStatus,
    destination,
    isDemoAdmin,
    timedOut,
    debugData,
    // Exposed so callers (e.g. checkout-success) can force a same-tab refresh
    // after a known billing-state change instead of waiting for the next
    // visibilitychange tick. Throttling lives on the visibility listener
    // only; explicit callers are trusted not to spam.
    refreshAuthDecision,
  };

  return (
    <AuthDecisionContext.Provider value={contextValue}>
      <AuthDebugOverlay label="AuthRouter — RESOLVED" data={debugData} />
      {children}
    </AuthDecisionContext.Provider>
  );
};

// ── ROOT REDIRECT ──────────────────────────────────────────────────────────
const RootRedirect = () => {
  const decision = useAuthDecision();
  if (!decision) return null;
  return <Navigate to={decision?.destination} replace />;
};

// ── REQUIRE AUTH ───────────────────────────────────────────────────────────
const RequireAuth = ({ children, allowedRoles }) => {
  const decision = useAuthDecision();
  const location = useLocation();

  if (!decision) {
    return (
      <div style={{ paddingTop: 60 }}>
        <AuthDebugOverlay
          label="RequireAuth — NO DECISION CONTEXT"
          data={{
            guard: 'RequireAuth',
            decisionContext: 'NULL — AuthRouter may not have wrapped this route',
            pathname: location?.pathname,
            allowedRoles: allowedRoles ? allowedRoles?.join(',') : 'any',
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
        </div>
      </div>
    );
  }

  const { user, userProfile } = decision;
  const role = userProfile?.role;
  const roleAllowed = !allowedRoles || !role || allowedRoles?.includes(role);
  const authDecision = !user ? 'REDIRECT_LOGIN' : !roleAllowed ? 'REDIRECT_DESTINATION' : 'ALLOW';

  console.log(`[RequireAuth] ${location?.pathname} — user:${!!user} role:${role} allowed:${roleAllowed} → ${authDecision}`);

  if (!user) return <Navigate to="/login" replace />;

  if (allowedRoles && role && !allowedRoles?.includes(role)) {
    return <Navigate to={decision?.destination} replace />;
  }

  return (
    <>
      <AuthDebugOverlay
        label={`RequireAuth — ${authDecision}`}
        data={{
          guard: 'RequireAuth',
          pathname: location?.pathname,
          authUser: user ? 'FOUND' : 'NOT FOUND',
          role: role || 'NOT FOUND',
          allowedRoles: allowedRoles ? allowedRoles?.join(',') : 'any',
          roleCheck: roleAllowed ? 'ALLOWED' : 'BLOCKED',
          authDecision,
          resolvedDestination: decision?.destination,
          hasCompany: decision?.hasCompany ? 'FOUND' : 'NOT FOUND',
          childWillRender: authDecision === 'ALLOW' ? 'YES' : 'NO',
        }}
      />
      {children}
    </>
  );
};

// ── REQUIRE SETUP ──────────────────────────────────────────────────────────
const RequireSetup = ({ children }) => {
  const decision = useAuthDecision();
  const location = useLocation();

  if (!decision) {
    return (
      <div style={{ paddingTop: 60 }}>
        <AuthDebugOverlay
          label="RequireSetup — NO DECISION CONTEXT"
          data={{
            guard: 'RequireSetup',
            decisionContext: 'NULL — AuthRouter may not have wrapped this route',
            pathname: location?.pathname,
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
        </div>
      </div>
    );
  }

  const { user, userProfile, hasCompany, subscriptionActive, isDemoAdmin } = decision;
  const role = userProfile?.role;

  if (!user) return <Navigate to="/login" replace />;
  if (!userProfile) return <Navigate to="/login" replace />;
  if (role !== 'admin') return <Navigate to={decision?.destination} replace />;
  if (isDemoAdmin) return <Navigate to="/admin-dashboard" replace />;

  if (subscriptionActive) {
    console.log('[RequireSetup] subscriptionActive=true → redirecting to /admin-dashboard');
    return <Navigate to="/admin-dashboard" replace />;
  }

  const setupDecision = 'ALLOW';
  console.log(`[RequireSetup] ${location?.pathname} — hasCompany:${hasCompany} subscriptionActive:${subscriptionActive} → ${setupDecision}`);

  return (
    <>
      <AuthDebugOverlay
        label={`RequireSetup — ${setupDecision}`}
        data={{
          guard: 'RequireSetup',
          pathname: location?.pathname,
          authUser: user ? 'FOUND' : 'NOT FOUND',
          role: role || 'NOT FOUND',
          hasCompany: hasCompany ? 'FOUND' : 'NOT FOUND',
          subscriptionActive,
          isDemoAdmin,
          setupDecision,
          childWillRender: 'YES',
        }}
      />
      {children}
    </>
  );
};

// ── OPS DASHBOARD GUARD ────────────────────────────────────────────────────
// The /ops routes are reachable only via direct URL (no nav entry, no
// roleAccess allowlist). This guard is the UX layer — silently redirects
// anyone who isn't on the allowlist. The real security boundary is the
// `ops-stats` edge function which re-checks OPS_ADMIN_EMAILS server-side;
// we mirror its secret here via VITE_OPS_ADMIN_EMAILS so there is a single
// place to add/remove an ops admin (update both env vars, no code push).
const OPS_ADMIN_EMAILS = (import.meta.env?.VITE_OPS_ADMIN_EMAILS || '')
  .split(',')
  .map((e) => e.trim().toLowerCase())
  .filter(Boolean);
const RequireOpsAdmin = ({ children }) => {
  const { user, userProfile, loading, roleLoading } = useAuth();
  if (loading || roleLoading) return null;
  if (!user) return <Navigate to="/login" replace />;
  const email = (userProfile?.email || user?.email || '').toLowerCase();
  if (!OPS_ADMIN_EMAILS.length || !OPS_ADMIN_EMAILS.includes(email)) {
    return <Navigate to="/" replace />;
  }
  return children;
};

// ── CHECKOUT SUCCESS HANDLER ───────────────────────────────────────────────
// Legacy handler — kept for backward compatibility with any ?checkout=success
// params that may still appear on /admin-dashboard from old payment links.
// The primary checkout success flow now goes through /checkout-success page.
const CheckoutSuccessHandler = () => {
  const { user, userProfile, loading, roleLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [handled, setHandled] = useState(false);

  const hasCheckoutParam = new URLSearchParams(location.search)?.get('checkout') === 'success';

  useEffect(() => {
    if (!hasCheckoutParam || handled) return;
    if (loading || roleLoading) return;
    if (!user || !userProfile) return;
    if (userProfile?.role !== 'admin') {
      navigate('/admin-dashboard', { replace: true });
      return;
    }

    const applySubscription = async () => {
      setHandled(true);
      // Bust the module-level AuthRouter cache so the webhook-written status is picked up
      if (user?.id && _resolvedCache?.[user?.id]) {
        delete _resolvedCache?.[user?.id];
      }
      // Strip the checkout param and stay on admin-dashboard.
      // subscription_status is set by stripe-webhook on checkout.session.completed,
      // NOT here — writing it here would allow bypass via abandoned checkouts.
      navigate('/admin-dashboard', { replace: true });
    };

    applySubscription();
  }, [hasCheckoutParam, handled, loading, roleLoading, user, userProfile]);

  // Only show loading spinner if we're actively handling the param
  if (!hasCheckoutParam || handled) return null;
  if (loading || roleLoading || !user || !userProfile) return null;

  return (
    <div className="flex items-center justify-center h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
    </div>
  );
};

// ── APP ROUTES ─────────────────────────────────────────────────────────────
// AuthRouter is rendered here as a STABLE WRAPPER around RouterRoutes.
// It is NOT inside any <Route> element, so React Router never remounts it
// during navigation. This is the critical architectural fix.
const AppRoutes = () => {
  return (
    <AuthRouter>
      <CheckoutSuccessHandler />
      <RouterRoutes>
        {/* ── PUBLIC ROUTES ── */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/demo-access" element={<DemoLogin />} />
        <Route path="/login" element={<Login />} />
        <Route path="/company-registration" element={<CompanyRegistration />} />
<Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsAndConditions />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/data-processing" element={<DataProcessingAgreement />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/email-verification-pending" element={<EmailVerificationPending />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/auth-callback" element={<AuthCallback />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        <Route path="/invite-setup" element={<InviteSetup />} />
        <Route path="/email-verified" element={<EmailVerified />} />
        <Route path="/checkout-success" element={<CheckoutSuccess />} />

        {/* ── SETUP FLOW ROUTES — Admin only, no company yet ── */}
        <Route path="/setup-flow-company-details" element={<RequireSetup><DebugPageMount name="SetupFlowCompanyDetails"><SetupFlowCompanyDetails /></DebugPageMount></RequireSetup>} />
        <Route path="/setup-flow-plan-confirmation" element={<RequireSetup><DebugPageMount name="SetupFlowPlanConfirmation"><SetupFlowPlanConfirmation /></DebugPageMount></RequireSetup>} />
        <Route path="/setup-flow-create-site" element={<RequireSetup><SetupFlowCreateSite /></RequireSetup>} />
        <Route path="/setup-flow-invite-team" element={<RequireSetup><SetupFlowInviteTeam /></RequireSetup>} />
        <Route path="/setup-flow-checklist-confirmation" element={<RequireSetup><SetupFlowChecklistConfirmation /></RequireSetup>} />

        {/* ── PROTECTED ROUTES ── */}
        {/* PaywallGuard wraps all protected routes — exempt routes are handled inside PaywallGuard */}
        <Route element={<PaywallGuard><Outlet /></PaywallGuard>}>

        {/* Sites */}
        <Route path="/sites" element={<RequireAuth allowedRoles={['admin', 'site_manager']}><SitesList /></RequireAuth>} />
        <Route path="/sites/:siteId" element={<RequireAuth><SiteDetailDashboard /></RequireAuth>} />
        <Route path="/sites/:siteId/settings" element={<RequireAuth allowedRoles={['admin']}><SiteSettings /></RequireAuth>} />
        <Route path="/sites/:siteId/documents/:documentId" element={<RequireAuth><DocumentDetail /></RequireAuth>} />
        <Route path="/documents/:id" element={<RequireAuth><DocumentDetail /></RequireAuth>} />

        {/* Admin Dashboard */}
        <Route path="/admin-dashboard" element={
          <RequireAuth allowedRoles={['admin']}>
            <DebugPageMount name="AdminDashboard">
              <AdminDashboard />
            </DebugPageMount>
          </RequireAuth>
        } />

        {/* Enhanced Admin Dashboard */}
        <Route path="/enhanced-admin-dashboard" element={
          <RequireAuth allowedRoles={['admin']}>
            <EnhancedAdminDashboard />
          </RequireAuth>
        } />

        {/* Site Manager Dashboard */}
        <Route path="/site-manager-dashboard" element={
          <RequireAuth allowedRoles={['admin', 'site_manager']}>
            <SiteManagerDashboard />
          </RequireAuth>
        } />

        {/* Report Builder */}
        <Route path="/report-builder" element={
          <RequireAuth allowedRoles={['admin', 'site_manager']}>
            <ReportBuilder />
          </RequireAuth>
        } />

        {/* Compliance Report */}
        <Route path="/compliance-report" element={
          <RequireAuth allowedRoles={['admin', 'site_manager', 'client']}>
            <ComplianceReport />
          </RequireAuth>
        } />

        {/* Budget Report */}
        <Route path="/budget-report" element={
          <RequireAuth allowedRoles={['admin', 'site_manager']}>
            <BudgetReport />
          </RequireAuth>
        } />

        {/* Notifications */}
        <Route path="/notifications-center" element={
          <RequireAuth allowedRoles={['admin', 'site_manager']}>
            <NotificationsCenter />
          </RequireAuth>
        } />

        {/* Worker Today - primary worker landing page */}
        <Route path="/worker-today" element={
          <RequireAuth allowedRoles={['worker']}>
            <WorkerHome />
          </RequireAuth>
        } />

        {/* Worker Home - safe post-invite landing page */}
        <Route path="/worker-home" element={
          <RequireAuth allowedRoles={['worker']}>
            <WorkerWelcomeLanding />
          </RequireAuth>
        } />

        {/* Legacy worker routes */}
        <Route path="/worker-dashboard" element={<Navigate to="/worker-today" replace />} />
        <Route path="/home" element={<Navigate to="/worker-today" replace />} />

        {/* Worker shorthand routes */}
        <Route path="/tasks" element={<Navigate to="/task-completion-workflow" replace />} />
        <Route path="/snags-create" element={<Navigate to="/snags" replace />} />
        <Route path="/documents" element={<Navigate to="/document-management" replace />} />
        <Route path="/profile" element={<Navigate to="/profile-settings" replace />} />

        {/* Document Management */}
        <Route path="/document-management" element={<RequireAuth allowedRoles={['admin', 'site_manager', 'worker']}><DocumentManagement /></RequireAuth>} />

        {/* Site Dashboard */}
        <Route path="/site-dashboard/:siteId?" element={<RequireAuth allowedRoles={['admin', 'site_manager', 'worker']}><SiteDashboard /></RequireAuth>} />

        {/* Site Setup Wizard */}
        <Route path="/site-setup-wizard" element={
          <RequireAuth allowedRoles={['admin']}>
            <SiteSetupWizard />
          </RequireAuth>
        } />

        {/* Incident Reporting */}
        <Route path="/incident-reporting" element={
          <RequireAuth allowedRoles={['admin', 'site_manager', 'worker']}>
            <IncidentReporting />
          </RequireAuth>
        } />

        {/* Daily Site Checklist */}
        <Route path="/daily-site-checklist" element={
          <RequireAuth allowedRoles={['admin', 'site_manager', 'worker']}>
            <DailySiteChecklist />
          </RequireAuth>
        } />

        {/* Audits */}
        <Route path="/audits-inspections" element={
          <RequireAuth allowedRoles={['admin', 'site_manager']}>
            <AuditsInspections />
          </RequireAuth>
        } />

        {/* Drawings */}
        <Route path="/drawings-plans" element={
          <RequireAuth allowedRoles={['admin', 'site_manager', 'worker']}>
            <DrawingsPlans />
          </RequireAuth>
        } />

        {/* Checklist Management */}
        <Route path="/checklist-management" element={
          <RequireAuth allowedRoles={['admin']}>
            <ChecklistManagement />
          </RequireAuth>
        } />

        {/* Workers List */}
        <Route path="/workers" element={
          <RequireAuth allowedRoles={['admin', 'site_manager']}>
            <WorkersList />
          </RequireAuth>
        } />
        <Route path="/workers/:workerId" element={
          <RequireAuth allowedRoles={['admin', 'site_manager']}>
            <WorkerDetail />
          </RequireAuth>
        } />

        {/* Admin Worker Assignment */}
        <Route path="/admin-worker-assignment" element={
          <RequireAuth allowedRoles={['admin']}>
            <AdminWorkerAssignment />
          </RequireAuth>
        } />

        {/* Camera Upload */}
        <Route path="/camera-first-photo-upload" element={
          <RequireAuth allowedRoles={['worker', 'site_manager']}>
            <CameraFirstPhotoUpload />
          </RequireAuth>
        } />

        {/* Task Management */}
        <Route path="/task-planner" element={
          <RequireAuth allowedRoles={['admin', 'site_manager']}>
            <TaskPlanner />
          </RequireAuth>
        } />
        <Route path="/task-completion-workflow" element={<RequireAuth allowedRoles={['admin', 'site_manager', 'worker']}><TaskCompletionWorkflow /></RequireAuth>} />
        <Route path="/task-analytics-dashboard" element={
          <RequireAuth allowedRoles={['admin', 'site_manager']}>
            <TaskAnalyticsDashboard />
          </RequireAuth>
        } />

        {/* Photo Upload */}
        <Route path="/photo-upload-prompt" element={
          <RequireAuth allowedRoles={['worker', 'site_manager', 'admin']}>
            <PhotoUploadPrompt />
          </RequireAuth>
        } />

        {/* Site Photo Gallery */}
        <Route path="/site-photo-gallery" element={<RequireAuth allowedRoles={['admin', 'site_manager', 'worker']}><SitePhotoGallery /></RequireAuth>} />

        {/* Snags */}
        <Route path="/snags" element={<RequireAuth allowedRoles={['admin', 'site_manager', 'worker', 'client']}><SnagsList /></RequireAuth>} />
        <Route path="/snags-list-creation" element={<Navigate to="/snags" replace />} />
        <Route path="/snags/:snagId" element={<RequireAuth><SnagDetailView /></RequireAuth>} />
        <Route path="/snag-detail-view" element={<RequireAuth allowedRoles={['admin', 'site_manager', 'worker']}><SnagDetailView /></RequireAuth>} />

        {/* Drawing Viewer */}
        <Route path="/drawing-viewer/:drawingId" element={
          <RequireAuth allowedRoles={['admin', 'site_manager', 'worker']}>
            <DrawingViewer />
          </RequireAuth>
        } />

        {/* Drawing Detail */}
        <Route path="/drawing-detail/:drawingId" element={
          <RequireAuth allowedRoles={['admin', 'site_manager', 'worker']}>
            <DrawingDetail />
          </RequireAuth>
        } />

        {/* Drawings Gallery */}
        <Route path="/drawings-gallery" element={
          <RequireAuth allowedRoles={['admin', 'site_manager', 'worker']}>
            <DrawingsGallery />
          </RequireAuth>
        } />

        {/* Client Dashboard */}
        <Route path="/client-dashboard" element={
          <RequireAuth allowedRoles={['client']}>
            <ClientDashboard />
          </RequireAuth>
        } />

        {/* Client Portal alias */}
        <Route path="/client-portal" element={<Navigate to="/client-dashboard" replace />} />

        {/* Profile Settings */}
        <Route path="/profile-settings" element={<RequireAuth><ProfileSettings /></RequireAuth>} />

        {/* Company Settings */}
        <Route path="/company-settings" element={
          <RequireAuth allowedRoles={['admin', 'site_manager']}>
            <CompanySettings />
          </RequireAuth>
        } />

        {/* Help & Support */}
        <Route path="/help" element={<RequireAuth><HelpSupport /></RequireAuth>} />

        {/* Invite Users */}
        <Route path="/invite-users" element={
          <RequireAuth allowedRoles={['admin', 'site_manager']}>
            <InviteUsers />
          </RequireAuth>
        } />

        {/* QA Testing Dashboard */}
        <Route path="/qa-testing-dashboard" element={
          <RequireAuth allowedRoles={['admin']}>
            <QATestingDashboard />
          </RequireAuth>
        } />

        {/* Subscription Management */}
        <Route path="/subscription-management" element={
          <RequireAuth allowedRoles={['admin']}>
            <SubscriptionManagement />
          </RequireAuth>
        } />

        {/* Invoice History */}
        <Route path="/invoice-history" element={
          <RequireAuth allowedRoles={['admin']}>
            <InvoiceHistory />
          </RequireAuth>
        } />

        {/* CSV Import Center */}
        <Route path="/csv-import-center" element={
          <RequireAuth allowedRoles={['admin']}>
            <CsvImportCenter />
          </RequireAuth>
        } />

        {/* RAMS Management */}
        <Route path="/rams-management" element={
          <RequireAuth allowedRoles={['admin', 'site_manager']}>
            <RamsManagement />
          </RequireAuth>
        } />

        {/* RAMS Worker View */}
        <Route path="/rams-worker-view" element={
          <RequireAuth allowedRoles={['worker']}>
            <RamsWorkerView />
          </RequireAuth>
        } />

        {/* Site Inductions Management */}
        <Route path="/site-inductions-management" element={
          <RequireAuth allowedRoles={['admin', 'site_manager']}>
            <SiteInductionsManagement />
          </RequireAuth>
        } />

        {/* Site Inductions Worker View */}
        <Route path="/site-inductions-worker-view" element={
          <RequireAuth allowedRoles={['worker']}>
            <SiteInductionsWorkerView />
          </RequireAuth>
        } />

        {/* Toolbox Talks Management */}
        <Route path="/toolbox-talks-management" element={
          <RequireAuth allowedRoles={['admin', 'site_manager']}>
            <ToolboxTalksManagement />
          </RequireAuth>
        } />

        {/* Toolbox Talks Worker View */}
        <Route path="/toolbox-talks-worker-view" element={
          <RequireAuth allowedRoles={['worker']}>
            <ToolboxTalksWorkerView />
          </RequireAuth>
        } />

        {/* Site Diary Management */}
        <Route path="/site-diary-management" element={
          <RequireAuth allowedRoles={['admin', 'site_manager']}>
            <SiteDiaryManagement />
          </RequireAuth>
        } />

        {/* Worker Certifications */}
        <Route path="/worker-certifications" element={
          <RequireAuth allowedRoles={['admin', 'site_manager']}>
            <WorkerCertificationsManagement />
          </RequireAuth>
        } />

        {/* Compliance Dashboard */}
        <Route path="/compliance-dashboard" element={
          <RequireAuth allowedRoles={['admin', 'site_manager']}>
            <ComplianceDashboard />
          </RequireAuth>
        } />

        {/* Near Misses */}
        <Route path="/near-misses" element={
          <RequireAuth allowedRoles={['admin', 'site_manager']}>
            <NearMissesPage />
          </RequireAuth>
        } />

        {/* Daily Site Report */}
        <Route path="/daily-site-report" element={
          <RequireAuth allowedRoles={['admin', 'site_manager', 'client']}>
            <DailySiteReport />
          </RequireAuth>
        } />

        {/* Admin Activity Log */}
        <Route path="/admin-activity-log" element={
          <RequireAuth allowedRoles={['admin']}>
            <AdminActivityLog />
          </RequireAuth>
        } />

        {/* Worker Schedule */}
        <Route path="/worker-schedule" element={
          <RequireAuth allowedRoles={['admin']}>
            <WorkerSchedule />
          </RequireAuth>
        } />

        </Route>{/* end PaywallGuard wrapper */}

        {/* Internal ops dashboard — Heidi-only, outside PaywallGuard */}
        <Route path="/ops" element={<RequireOpsAdmin><OpsOverview /></RequireOpsAdmin>} />
        <Route path="/ops/:companyId" element={<RequireOpsAdmin><OpsCompanyDetail /></RequireOpsAdmin>} />

        {/* Catch-all → resolved destination */}
        <Route path="*" element={<RootRedirect />} />
      </RouterRoutes>
    </AuthRouter>
  );
};

const Routes = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ErrorBoundary>
          <ScrollToTop />
          <AppRoutes />
        </ErrorBoundary>
      </AuthProvider>
      <PWASplashScreen />
    </BrowserRouter>
  );
};

export default Routes;
